import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, ArrowUpRight, ArrowDownLeft, RefreshCw, Archive, Search } from 'lucide-react';
import { getInventoryMovements, createInventoryMovement, getInventoryItems } from '../services/api';
import CreateMovementModal from '../components/CreateMovementModal';
import './InventoryMovementsPage.css';

function InventoryMovementsPage() {
  const { t } = useTranslation();
  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterItemId, setFilterItemId] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [movementsData, itemsData] = await Promise.all([
        getInventoryMovements({ itemId: filterItemId }), // Pass filter if selected
        getInventoryItems(),
      ]);
      setMovements(Array.isArray(movementsData) ? movementsData : []);
      setItems(itemsData?.items || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterItemId]);

  const handleCreateMovement = async (movementData) => {
    await createInventoryMovement(movementData);
    fetchData(); // Refresh list
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'STOCK_IN':
        return <ArrowDownLeft size={20} className="icon-in" />;
      case 'STOCK_OUT':
        return <ArrowUpRight size={20} className="icon-out" />;
      case 'ADJUSTMENT':
        return <RefreshCw size={20} className="icon-adj" />;
      default:
        return <Archive size={20} />;
    }
  };

  const getMovementLabel = (type) => {
    switch (type) {
      case 'STOCK_IN':
        return t('inventory.movementsPage.typeStockIn');
      case 'STOCK_OUT':
        return t('inventory.movementsPage.typeStockOut');
      case 'ADJUSTMENT':
        return t('inventory.movementsPage.typeAdjustment');
      default:
        return type;
    }
  };

  // Helper to find item name
  const getItemName = (itemId) => {
    const item = items.find((i) => i.itemId === itemId);
    return item ? item.name : t('inventory.movementsPage.unknownItem');
  };

  const getItemUnit = (itemId) => {
    const item = items.find((i) => i.itemId === itemId);
    return item ? item.unit : '';
  };

  return (
    <div className="movements-page">
      <div className="page-header">
        <div>
          <h1>{t('inventory.movementsPage.title')}</h1>
          <p className="subtitle">{t('inventory.movementsPage.subtitle')}</p>
        </div>
        <button type="button" className="primary-button" onClick={() => setIsModalOpen(true)}>
          <Plus size={20} />
          {t('inventory.movementsPage.registerMovement')}
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <select
            value={filterItemId}
            onChange={(e) => setFilterItemId(e.target.value)}
            className="item-filter-select"
          >
            <option value="">{t('inventory.movementsPage.allProducts')}</option>
            {items.map((item) => (
              <option key={item.itemId} value={item.itemId}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="movements-list-container">
        {loading ? (
          <div className="loading">{t('inventory.movementsPage.loading')}</div>
        ) : movements.length === 0 ? (
          <div className="empty-state">
            <Archive size={48} />
            <p>{t('inventory.movementsPage.empty')}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="movements-table">
              <thead>
                <tr>
                  <th>{t('inventory.movementsPage.date')}</th>
                  <th>{t('inventory.movementsPage.type')}</th>
                  <th>{t('inventory.movementsPage.product')}</th>
                  <th>{t('inventory.movementsPage.quantity')}</th>
                  <th>{t('inventory.movementsPage.previousStock')}</th>
                  <th>{t('inventory.movementsPage.newStock')}</th>
                  <th>{t('inventory.movementsPage.reason')}</th>
                  <th>{t('inventory.movementsPage.user')}</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <tr key={movement.SK}>
                    {' '}
                    {/* SK is unique per movement */}
                    <td>{new Date(movement.CreatedAt).toLocaleString()}</td>
                    <td>
                      <div
                        className={`movement-type-badge ${movement.Type.toLowerCase().replace('_', '-')}`}
                      >
                        {getMovementIcon(movement.Type)}
                        <span>{getMovementLabel(movement.Type)}</span>
                      </div>
                    </td>
                    <td className="font-medium">{getItemName(movement.ItemId)}</td>
                    <td className={movement.Quantity > 0 ? 'text-green' : 'text-red'}>
                      {movement.Quantity > 0 ? '+' : ''}
                      {movement.Quantity} {getItemUnit(movement.ItemId)}
                    </td>
                    <td className="text-secondary">{movement.StockBefore}</td>
                    <td className="font-bold">{movement.StockAfter}</td>
                    <td className="text-secondary">{movement.Reason || '-'}</td>
                    <td className="text-sm text-gray">
                      {movement.UserId || t('inventory.movementsPage.system')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateMovementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleCreateMovement}
        items={items}
      />
    </div>
  );
}

export default InventoryMovementsPage;
